import React, { useState, useEffect, useRef } from "react";
import ReactCardFlip from "react-card-flip";
import {
  Box,
  VStack,
  Text,
  IconButton,
  Heading,
  Image,
  Badge,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { FaStore, FaGlobe, FaGoogle } from "react-icons/fa";
import { FaHeart, FaRegHeart } from "react-icons/fa";

import { FaCircleArrowRight, FaCircleArrowLeft } from "react-icons/fa6";

import { useBreakpointValue } from "@chakra-ui/react";
import { getCategoryImage } from "../utils/categoryImage";

// Card surface — width fills its grid cell (capped on the outer wrapper)
// so it never overflows on mobile. Height fills the grid row, so cards in
// the same row stretch to match the tallest card's content height (default
// CSS Grid `align-items: stretch`). In a single-column row, that just means
// each card sizes to its own content.
const cardSurface = {
  width: "100%",
  h: "100%",
  borderRadius: "xl",
  bg: "white",
  boxShadow: "xl",
  border: "1px",
  borderColor: "gray.300",
};

const BusinessCard = ({
  business,
  translations,
  redeemed,
  showFavourite = false,
  isFavourite = false,
  onToggleFavourite,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const iconSize = useBreakpointValue({ base: "40px", md: "60px" });
  // Coupon title is now the smaller text on the card; business name above
  // takes the larger size. Long titles still shrink one step.
  const [titleFontSize, setTitleFontSize] = useState({
    base: "md",
    md: "lg",
  });
  const titleRef = useRef(null);

  useEffect(() => {
    const checkTitleHeight = () => {
      if (titleRef.current) {
        const lineHeight = parseInt(
          window.getComputedStyle(titleRef.current).lineHeight
        );
        const elementHeight = titleRef.current.offsetHeight;
        const numberOfLines = elementHeight / lineHeight;

        if (numberOfLines > 2) {
          setTitleFontSize({ base: "sm", md: "md" });
        } else {
          setTitleFontSize({ base: "md", md: "lg" });
        }
      }
    };

    checkTitleHeight();
    // Add resize listener
    window.addEventListener("resize", checkTitleHeight);
    return () => window.removeEventListener("resize", checkTitleHeight);
  }, [business.coupon.title]);

  const handleFlip = (e) => {
    e.stopPropagation();
    setIsFlipped(!isFlipped);
  };

  // Hide the flip control entirely when there are no terms/exclusions to
  // show — the back face would just say "No specific terms", which isn't
  // worth a flip.
  const hasTerms = Boolean(
    business.coupon.termsAndExclusions &&
      String(business.coupon.termsAndExclusions).trim()
  );

  // Front of the card
  const CardFront = () => (
    <Box
      {...cardSurface}
      overflow="hidden"
      position="relative"
      display="flex"
      flexDirection="column"
    >
      {/* Add redeemed badge if redeemed */}
      {redeemed && (
        <Badge
          position="absolute"
          top={4}
          right={4}
          colorScheme="red"
          fontSize="sm"
          zIndex={1}
          px={3}
          py={1}
          borderRadius="full"
        >
          {translations?.redeemed || "Redeemed"}
        </Badge>
      )}

      <Flex
        direction="column"
        flex={1}
        position="relative"
        opacity={redeemed ? 0.6 : 1}
        filter={redeemed ? "grayscale(100%)" : "none"}
      >
        {/* Hero Image Section */}
        <Box height="175px" position="relative" flexShrink={0}>
          {(() => {
            // Prefer the uploaded business logo. If none, fall back to a
            // deterministic-but-varied category image (see
            // utils/categoryImage). If neither exists, show the storefront
            // icon placeholder.
            const fallbackCategoryImage = !business.imageUrl
              ? getCategoryImage(
                  // categoryKey carries the raw {en, fr} so the slug stays in
                  // English regardless of viewer language; category (display)
                  // is the localized fallback.
                  business.categoryKey ?? business.category,
                  business.id
                )
              : null;
            const resolvedImage = business.imageUrl || fallbackCategoryImage;
            return resolvedImage ? (
              <Image
                src={resolvedImage}
                alt={business.name}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            ) : (
              <Flex
                w="100%"
                h="100%"
                bg="gray.100"
                justifyContent="center"
                alignItems="center"
              >
                <FaStore size={iconSize} color="gray.400" />
              </Flex>
            );
          })()}

          {/* Category chip — overlaid on the image, upper-left */}
          {business.category && (
            <Badge
              position="absolute"
              top={3}
              left={3}
              variant="subtle"
              colorScheme="green"
              fontSize="xs"
              borderRadius="full"
              textTransform="none"
              fontWeight="normal"
              px={2}
              py={1}
              boxShadow="sm"
              zIndex={1}
            >
              {business.category}
            </Badge>
          )}

          {/* Add Favourite Button */}
          {showFavourite && (
            <IconButton
              icon={isFavourite ? <FaHeart /> : <FaRegHeart />}
              position="absolute"
              bottom="-20px"
              right="6"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavourite?.();
              }}
              aria-label={
                isFavourite ? "Remove from favourites" : "Add to favourites"
              }
              variant="ghost"
              color={isFavourite ? "red.500" : "gray.600"}
              size="lg"
              bg="white"
              borderRadius="full"
              boxShadow="md"
              _hover={{
                transform: "scale(1.1)",
                color: "red.500",
              }}
              transition="all 0.2s"
              zIndex={2}
            />
          )}
        </Box>

        {/* Content Section — grows to fill remaining space */}
        <VStack align="stretch" spacing={2} pt={3} px={6} pb={3} flex={1}>
          {/* Business Info */}
          <VStack align="start" spacing={1} mb={2}>
            <Text
              fontSize={{ base: "1.35rem", md: "1.6875rem" }}
              fontWeight="semibold"
              color="gray.800"
              textAlign="left"
              width="100%"
              lineHeight="1.2"
            >
              {business.name}
            </Text>
          </VStack>

          {/* Offer Details */}
          <VStack align="start" spacing={0}>
            <Heading
              ref={titleRef}
              fontSize={titleFontSize}
              fontWeight="bold"
              color="gray.800"
              lineHeight="1.1"
              textAlign="left"
              w="100%"
            >
              {business.coupon.title}
            </Heading>

            <Text
              fontSize={{ base: "sm", md: "sm" }}
              color="gray.600"
              lineHeight="tall"
              textAlign="left"
              pt={1}
            >
              {business.coupon.description}
            </Text>
          </VStack>
        </VStack>

        {/* Bottom row: maxValue (left) + Website / Google / Flip Arrow (right) */}
        <Flex
          px={6}
          pb={4}
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexShrink={0}
        >
          {business.coupon.maxValue > 0 ? (
            <Flex alignItems="baseline" gap={2} minW={0}>
              <Text fontSize="sm" color="gray.600">
                {translations?.maxValue || "Maximum value"}:
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="semibold"
                color="gray.800"
              >
                {translations?.language === "fr"
                  ? `${Number(business.coupon.maxValue).toLocaleString(
                      "fr-CA"
                    )} $`
                  : `$${Number(business.coupon.maxValue).toLocaleString(
                      "en-US"
                    )}`}
              </Text>
            </Flex>
          ) : (
            <Box />
          )}

          <Flex alignItems="center" gap={3} flexShrink={0}>
            {business.websiteUrl && (
              <IconButton
                icon={<FaGlobe size="22px" />}
                aria-label={translations?.visitWebsite || "Visit Website"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(business.websiteUrl, "_blank");
                }}
                variant="ghost"
                size="sm"
                minW="auto"
                height="auto"
                p={0}
              />
            )}
            {business.googleProfileUrl && (
              <IconButton
                icon={<FaGoogle size="22px" />}
                aria-label={translations?.googleProfile || "Google Profile"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(business.googleProfileUrl, "_blank");
                }}
                variant="ghost"
                size="sm"
                minW="auto"
                height="auto"
                p={0}
              />
            )}
            {hasTerms && (
              <Box
                as={FaCircleArrowRight}
                size="22px"
                color="black"
                cursor="pointer"
                onClick={handleFlip}
                aria-label="Show more info"
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );

  // Back of the card
  const CardBack = () => (
    <Box
      {...cardSurface}
      overflow="hidden"
      position="relative"
      display="flex"
      flexDirection="column"
    >
      {/* Add redeemed badge if redeemed */}
      {redeemed && (
        <Badge
          position="absolute"
          top={4}
          right={4}
          colorScheme="red"
          fontSize="sm"
          zIndex={1}
          px={3}
          py={1}
          borderRadius="full"
        >
          {translations?.redeemed || "Redeemed"}
        </Badge>
      )}

      <Flex
        direction="column"
        flex={1}
        p={6}
        gap={6}
        opacity={redeemed ? 0.6 : 1}
        filter={redeemed ? "grayscale(100%)" : "none"}
      >
        <Heading size="md" alignSelf="flex-start">
          {business.name}
        </Heading>

        <Divider borderColor="gray.300" />

        {/* Terms and Conditions */}
        <VStack align="start" spacing={2} flex={1}>
          <Text fontWeight="bold">
            {translations?.termsAndExclusions || "Terms & Exclusions"}:
          </Text>
          <Text fontSize="sm" color="gray.700" textAlign="left">
            {business.coupon.termsAndExclusions ||
              translations?.noSpecificTerms ||
              "No specific terms"}
          </Text>
        </VStack>

        {/* Reserve space so the absolute-positioned flip arrow doesn't
            overlap the terms text */}
        <Box height="22px" flexShrink={0} />
      </Flex>
    </Box>
  );

  return (
    <Box width="100%" maxW="360px" margin="0 auto" h="100%">
      <ReactCardFlip
        isFlipped={isFlipped}
        flipDirection="horizontal"
        containerStyle={{ height: "100%" }}
      >
        {/* Front Side — flip arrow now lives inside CardFront's bottom row */}
        <Box position="relative" h="100%">
          <CardFront />
        </Box>

        {/* Back Side */}
        <Box position="relative" h="100%">
          <CardBack />
          {hasTerms && (
            <Box
              as={FaCircleArrowLeft}
              position="absolute"
              bottom={4}
              right={4}
              size="22px"
              color="black"
              cursor="pointer"
              onClick={handleFlip}
              aria-label="Show front"
            />
          )}
        </Box>
      </ReactCardFlip>
    </Box>
  );
};

export default BusinessCard;
